import type { AeropayBankAccount } from '@iheartjane/data-access';
import { useAeropayUser } from '@iheartjane/data-access/react-query';
import {
  Banner,
  Box,
  Button,
  ErrorIcon,
  Flex,
  List,
  Loading,
  Typography,
} from '@iheartjane/reefer';
import { useEffect, useMemo, useState } from 'react';

import { getJwt } from '@jane/shared/auth';
import { useJaneUser } from '@jane/shared/data-access';

import {
  BankAccountCard,
  FakeRadio,
} from '../selectBankAccountModal/selectBankAccountModal.styles';

export const BankSelectionScreen = ({
  onDoneClick,
}: {
  onDoneClick: (selectedBank: AeropayBankAccount) => void;
}) => {
  const {
    data: janeUser,
    isPending: janeUserLoading,
    error: userError,
  } = useJaneUser();

  const {
    data: aeropayUser,
    error: aeropayUserError,
    isPending: aeropayUserLoading,
  } = useAeropayUser({
    janeUserId: janeUser?.user?.id || null,
    jwt: getJwt(),
  });
  const [bankAccountsList, setBankAccountsList] = useState<
    AeropayBankAccount[]
  >([]);

  const dataErrors = useMemo(() => {
    return userError || aeropayUserError;
  }, [userError, aeropayUserError]);

  useEffect(() => {
    if (!aeropayUserLoading && !janeUserLoading) {
      setBankAccountsList(aeropayUser?.bank_accounts || []);
    }
  }, [aeropayUser, aeropayUserLoading, janeUserLoading]);

  const updateIsSelected = (last4Digits: string) => {
    setBankAccountsList((prevBankAccountsList) =>
      prevBankAccountsList.map((bankAccount) => ({
        ...bankAccount,
        is_selected: bankAccount.account_last_4 === last4Digits,
      }))
    );
  };

  const findIsSelected = () =>
    bankAccountsList.find((bankAccount) => bankAccount.is_selected === true) ||
    bankAccountsList[0];

  if (janeUserLoading || aeropayUserLoading) {
    return (
      <Box py={48} position="relative">
        <Loading color="black" size="lg" />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="title-bold">Choose your account</Typography>
      <Typography mt={12}>Select an account to use with Jane Pay</Typography>
      {dataErrors && (
        <Banner
          mt={12}
          icon={<ErrorIcon />}
          variant="error"
          label={String(dataErrors)}
        />
      )}
      <List label="Bank Accounts">
        {bankAccountsList.map((bankAccount) => (
          <List.Item key={bankAccount.account_last_4} px={0} py={8}>
            <BankAccountCard
              borderWidth="2px"
              border={bankAccount.is_selected ? 'primary' : 'grays-ultralight'}
              width="100%"
              flat
            >
              <Flex
                alignItems="center"
                aria-label={`${bankAccount.bank_name}, ${bankAccount.name}`}
                onClick={() => updateIsSelected(bankAccount.account_last_4)}
              >
                <Box height={24} position="relative">
                  <FakeRadio
                    position="relative"
                    height={24}
                    width={24}
                    selected={bankAccount.is_selected}
                  >
                    <Box
                      height={12}
                      width={12}
                      left={4}
                      top={4}
                      position="absolute"
                      background="grays-white"
                    />
                  </FakeRadio>
                </Box>
                <Typography ml={16}>{bankAccount.name}</Typography>
              </Flex>
            </BankAccountCard>
          </List.Item>
        ))}
      </List>
      <Button
        mt={24}
        full
        label="Confirm account"
        onClick={() => onDoneClick(findIsSelected())}
      />
    </>
  );
};
